import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LinkedinIcon, Youtube as YoutubeIcon, Instagram as InstagramIcon, LogOut } from "lucide-react";
import { useState, useRef, useLayoutEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { PartnerNavbar } from "@/components/PartnerNavbar";
import { isNative } from "@/lib/native/platform";

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { toast } = useToast();
  const { user, signOut } = useAuth();
  // Only the native app hides the web chrome and uses the mobile shell. The public website — including
  // phone browsers, signed-in or not — always keeps the marketing header + PartnerNavbar, so the site
  // never mirrors the app. (The duplicate PartnerNavbar render is removed everywhere — P0-IA-01.)
  const hideWebChrome = isNative();

  // The fixed header's height is NOT a constant: on narrow screens the marketing nav and the
  // PartnerNavbar both wrap onto multiple rows, so the old static pt-16/pt-40 left content
  // rendering underneath the bars. Measure the real header and reserve exactly that much space.
  const headerRef = useRef<HTMLElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useLayoutEffect(() => {
    if (hideWebChrome) return;
    const el = headerRef.current;
    if (!el) return;
    const update = () => setHeaderHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [hideWebChrome, user]);


  const handleLogout = async () => {
    try {
      await signOut();
      toast({
        title: "Signed out successfully",
      });
      // Redirect to homepage after logout
      window.location.href = '/';
    } catch (error) {
      toast({
        title: "Logout Error",
        description: "Failed to sign out",
        variant: "destructive",
      });
    }
  };

  const navigation = [
    { name: "Home", href: "/" },
    { name: "Portfolios", href: "/portfolios" },
    { name: "About", href: "/about" },
    { name: "Our Team", href: "/team" },
    { name: "AWM Group", href: "/awm-group" },
    { name: "Blog", href: "/blog" },
    { name: "Podcast", href: "/podcast" },
    { name: "Contact", href: "/contact" },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <div className="min-h-screen bg-background">
      {/* Header — hidden only in the native app (mobile shell); always shown on the website */}
      {!hideWebChrome && (
      <header ref={headerRef} className="fixed top-0 left-0 right-0 w-full bg-card/95 backdrop-blur-sm shadow-sm border-b z-50">
        <nav className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 max-w-7xl mx-auto">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img 
                  src="/lovable-uploads/54df1e96-954a-49cb-be01-23aa956a5b19.png" 
                  alt="CBAM Logo" 
                  className="h-8 w-auto"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-center justify-center space-x-4">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
                      isActive(item.href)
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Social Links & CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <div className="flex items-center gap-3">
                <a 
                  href="https://www.linkedin.com/company/cape-berkshire"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <LinkedinIcon className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.youtube.com/channel/UCOIb5G5C3nbvQ3cImNKTURw"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <YoutubeIcon className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.instagram.com/cape_berkshire/?hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <InstagramIcon className="h-5 w-5" />
                </a>
                <a 
                  href="https://open.spotify.com/show/0SClIaFLzaOt2844wjsKY2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                </a>
              </div>
              {user ? (
                <Button variant="outline" size="sm" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              ) : (
                <Button variant="outline" size="sm" asChild>
                  <Link to="/partner/login">Partner Login</Link>
                </Button>
              )}
              <Button asChild>
                <Link to="/contact">Get Started</Link>
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                      isActive(item.href)
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent"
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className="pt-2 space-y-2">
                  {user ? (
                    <>
                      <Button variant="outline" asChild className="w-full">
                        <Link to="/partner/dashboard" onClick={() => setIsMenuOpen(false)}>
                          Dashboard
                        </Link>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => {
                          handleLogout();
                          setIsMenuOpen(false);
                        }}
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" asChild className="w-full">
                      <Link to="/partner/login" onClick={() => setIsMenuOpen(false)}>
                        Partner Login
                      </Link>
                    </Button>
                  )}
                  <Button asChild className="w-full">
                    <Link to="/contact" onClick={() => setIsMenuOpen(false)}>
                      Get Started
                    </Link>
                  </Button>
                  <div className="flex justify-center gap-4 pt-2">
                    <a 
                      href="https://www.linkedin.com/company/cape-berkshire"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <LinkedinIcon className="h-5 w-5" />
                    </a>
                    <a 
                      href="https://www.youtube.com/channel/UCOIb5G5C3nbvQ3cImNKTURw"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <YoutubeIcon className="h-5 w-5" />
                    </a>
                    <a 
                      href="https://www.instagram.com/cape_berkshire/?hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <InstagramIcon className="h-5 w-5" />
                    </a>
                    <a 
                      href="https://open.spotify.com/show/0SClIaFLzaOt2844wjsKY2"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </nav>
        
        {/* Partner Navbar — single render (double-render removed for the mobile redesign, P0-IA-01) */}
        {user && <PartnerNavbar />}
      </header>
      )}

      {/* Main Content.
          Web (header visible): reserve the MEASURED header height so wrapped mobile rows of the
          marketing nav and the PartnerNavbar never overlap page content (static pt-16/pt-40 could
          not cover the wrapped case). pt-16/pt-40 remain as the pre-measurement fallback.
          Mobile shell (header hidden — native app only): the web header is gone, so
          without this the content would start at y=0 and, under edge-to-edge (StatusBar overlay:true),
          render UP UNDER the system status bar. Portal pages that route through <Layout> (e.g. the
          Library and Research Hub tabs) have only a small py-* of their own and no safe-area inset, so
          fix it here at the shell level — one change covers every Layout-hosted page. The dedicated
          Mobile* screens don't use <Layout>; they already carry their own pt-[…safe-area-inset-top]. */}
      <main
        className={hideWebChrome ? "pt-[env(safe-area-inset-top)]" : headerHeight ? undefined : user ? "pt-40" : "pt-16"}
        style={!hideWebChrome && headerHeight ? { paddingTop: headerHeight } : undefined}
      >
        {children}
      </main>



      {/* Footer — hidden when the mobile shell is active */}
      {!hideWebChrome && (
      <footer className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-lg font-semibold mb-4">Cape Berkshire Asset Management</h3>
              <p className="text-primary-foreground/80 max-w-md">
                Professional investment portfolio management services. Cape Berkshire Asset Management is a trading name of Ascot Wealth Management.
              </p>
              <p className="text-primary-foreground/60 text-sm mt-2">
                FCA Number 551744 | Company No. 07428369 | London Road, Sunningdale, Berkshire, SL5 0ER
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-primary-foreground/80">
                <li><Link to="/portfolios" className="hover:text-primary-foreground">Model Portfolios</Link></li>
                <li><Link to="/portfolios" className="hover:text-primary-foreground">Investment Management</Link></li>
                <li><Link to="/portfolios" className="hover:text-primary-foreground">Financial Planning</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-primary-foreground/80">
                <li><Link to="/about" className="hover:text-primary-foreground">About Us</Link></li>
                <li><Link to="/team" className="hover:text-primary-foreground">Our Team</Link></li>
                <li><Link to="/blog" className="hover:text-primary-foreground">Blog</Link></li>
                <li><Link to="/contact" className="hover:text-primary-foreground">Contact</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-primary-foreground">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center text-primary-foreground/80">
            <p>&copy; {new Date().getFullYear()} Ascot Wealth Management Ltd. (trading as Cape Berkshire Asset Management). All rights reserved. Authorised and regulated by the FCA.</p>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
};